import { useEffect, useRef, useState } from "react";
import { DateDisplay } from "../../../../../../commonComponents/inline/dateDisplay/dateDisplay";
import { ArticleExtraData } from "../../../../../../../articlePage/stateInterfaces";
import { SiteItemData } from "../../../../../../interfaces";
import { disablePageRefresh, enablePageRefresh } from "../../../../../../siteWidgets/siteWidgetUtils";
import { FlashesMediaItem } from "../../../../../verticalVideoOverlayPlayer/designs/flashesMediaItem/flashesMediaItem";
import { VerticalVideoOverlayPlayer } from "../../../../../verticalVideoOverlayPlayer/verticalVideoOverlayPlayer";
import { getTranslation } from "../../../../autoTickerRowWidget/autoTickerRowTranslations";
import { categoryObj } from "../../../../newYnetTvVerticalDynamic/components/NewYnetTvVerticalDynamicMutamComponenta";
import { getImageUrl } from "config/apiUrls";
import { isWcmEditor } from "../../../../../../../config/serverConfig";
import { minutesDiff } from "../../newsFlashAndTwentyFourSevenMobileComponenta";
import { mapArticleExtraDataToOverlayItems } from "../utils/newsFlashUtils";
require("./m_ynet_ongoingRoofEventPopup.less");

interface OngoingRoofEventPopupProps {
    isOpen: boolean
    roof: string
    articles: ArticleExtraData[]
    isLTR: boolean
    domain: string
    languageCode: string
    category: categoryObj
    highlightingDuration: number
    verticalBackgroundImage?: string
    onClose: () => void
}

const LIVE_UPDATES_LABEL = "לעדכונים שוטפים";
const CLOSE_ANIMATION_MS = 280;
const mediaAspectRatios = {
    "6:7": { w: 215, h: 251 },
    "16:9": { w: 448, h: 251 },
    "9:16": { w: 207, h: 370 },
};

function getPosterUrl(article: ArticleExtraData) {
    const { specialData, promotionImageDetails, video } = article;
    if (!specialData) {
        return undefined;
    }
    const { mediaLinkType, mediaProportion } = specialData;
    const mediaPosterSize = mediaLinkType !== "none" && mediaLinkType !== undefined && mediaProportion
        ? mediaAspectRatios[mediaProportion]
        : { w: 448, h: 251 };

    if (mediaLinkType === "image" && promotionImageDetails && (promotionImageDetails.url || promotionImageDetails.publishedLink)) {
        return getImageUrl(promotionImageDetails, mediaPosterSize);
    }
    if (mediaLinkType === "video" && video && video[0] && video[0].posterSrc) {
        return video[0].posterSrc;
    }
    return undefined;
}

export const OngoingRoofEventPopup = (props: OngoingRoofEventPopupProps) => {
    const { isOpen, roof, articles, isLTR, domain, languageCode, category, highlightingDuration, verticalBackgroundImage, onClose } = props;

    const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
    const [selectedVideo, setSelectedVideo] = useState(-1);
    const [isMediaOverlayOpen, setIsMediaOverlayOpen] = useState(false);
    const [dragStartY, setDragStartY] = useState<number | null>(null);
    const [dragOffsetY, setDragOffsetY] = useState(0);
    const [isClosing, setIsClosing] = useState(false);
    const closeTimerRef: any = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (isOpen) {
            setExpandedItemId(null);
            setDragOffsetY(0);
            setIsClosing(false);
            disablePageRefresh();
        } else if (!isMediaOverlayOpen) {
            enablePageRefresh();
        }
    }, [isOpen]);

    useEffect(() => {
        return () => {
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
            }
            if (isOpen && !isMediaOverlayOpen) {
                enablePageRefresh();
            }
        };
    }, [isOpen, isMediaOverlayOpen]);

    const requestClose = () => {
        if (isClosing) {
            return;
        }
        setIsClosing(true);
        setDragStartY(null);
        setDragOffsetY(0);
        closeTimerRef.current = setTimeout(() => {
            onClose();
        }, CLOSE_ANIMATION_MS);
    };

    const toggleItem = (articleId: string, title: string) => {
        const currentlyOpen = expandedItemId === articleId;
        setExpandedItemId(currentlyOpen ? null : articleId);
        document.dispatchEvent(
            new CustomEvent("newsFlashesTwentyFourSevenReadMoreChange", {
                detail: {
                    action: currentlyOpen ? "Collapse" : "Expand",
                    label: title,
                },
            }),
        );
    };

    const openMedia = (index: number) => {
        setIsMediaOverlayOpen(true);
        setSelectedVideo(index);
        disablePageRefresh();
    };

    const closeMedia = () => {
        setIsMediaOverlayOpen(false);
        setSelectedVideo(-1);
        if (isOpen) {
            disablePageRefresh();
        } else {
            enablePageRefresh();
        }
    };

    const onHandleTouchStart = (e: React.TouchEvent) => {
        if (isClosing) {
            return;
        }
        setDragStartY(e.touches[0].clientY);
    };

    const onHandleTouchMove = (e: React.TouchEvent) => {
        if (dragStartY == null || isClosing) {
            return;
        }
        const delta = e.touches[0].clientY - dragStartY;
        if (delta > 0) {
            setDragOffsetY(delta);
        }
    };

    const onHandleTouchEnd = () => {
        if (dragOffsetY > 80) {
            requestClose();
        } else {
            setDragOffsetY(0);
        }
        setDragStartY(null);
    };

    const onBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            requestClose();
        }
    };

    const renderDate = (article: ArticleExtraData) => {
        const lang = languageCode ? languageCode.slice(0, 2) : "he";
        const isRecentItem = highlightingDuration != null && minutesDiff(article) < (highlightingDuration || 1);
        return isRecentItem
            ? getTranslation(lang, "Now")
            : <DateDisplay date={article.dateUpdatedOnSite} isMinutesAgo={true} isArticle={true} />;
    };

    const renderItem = (article: ArticleExtraData, index: number, isFirst: boolean) => {
        const { specialData, video, promotionImageDetails, title, plainText, author, articleId } = article;
        const mediaLinkType = specialData && specialData.mediaLinkType;
        const mediaProportion = specialData && specialData.mediaProportion;
        const posterUrl = getPosterUrl(article);
        const hasImage = !!posterUrl;
        const itemIsOpen = expandedItemId === articleId;
        const isPortrait = mediaProportion === "9:16";
        const mediaId = !isWcmEditor() && mediaLinkType === "video" && video && video[0] && video[0].mediaVideoId
            ? video[0].mediaVideoId
            : "";
        const mediaCredit = mediaLinkType === "video"
            ? ((specialData && specialData.videoCredit) || (video && video[0] && video[0].credit ? video[0].credit : undefined))
            : promotionImageDetails && promotionImageDetails.credit
                ? promotionImageDetails.credit
                : undefined;
        const isAuthor = author && author.length > 0;

        return (
            <div
                key={articleId}
                className={`ongoing-popup-item ${itemIsOpen ? "open" : ""} ${isFirst ? "is-first" : ""} ${hasImage ? "hasImage" : ""}`}
            >
                <div className="ongoing-popup-item-timeline" aria-hidden="true">
                    <div className={`ongoing-popup-dot ${isFirst ? "filled" : "white"}`} />
                </div>

                <div className="ongoing-popup-item-body">
                    <div className="ongoing-popup-item-header">
                        {hasImage &&
                            <div className={`ongoing-popup-image ${isPortrait ? "portrait" : ""}`} onClick={() => openMedia(index)} data_mediaid_analitycs={mediaId}>
                                <img className="posterImage" src={posterUrl} alt={title} />
                                {mediaLinkType === "video" && <div className="video-icon" />}
                                {mediaLinkType === "image" && <div className="expand-image-icon" />}
                            </div>
                        }

                        <div className="ongoing-popup-item-text" onClick={() => toggleItem(articleId, title)}>
                            <div className="ongoing-popup-item-title">{title}</div>
                            {!itemIsOpen && <div className="ongoing-popup-item-date">{renderDate(article)}</div>}
                        </div>
                    </div>

                    {itemIsOpen &&
                        <div className="ongoing-popup-item-content" onClick={() => toggleItem(articleId, title)}>
                            <div className="ongoing-popup-item-plain">{plainText}</div>

                            {isAuthor &&
                                <div className="ongoing-popup-item-author">
                                    {`(${author}${mediaCredit ? `, ${mediaCredit}` : ""})`}
                                </div>
                            }

                            <div className="ongoing-popup-item-date">{renderDate(article)}</div>
                        </div>
                    }
                </div>
            </div>
        );
    };

    if (!isOpen) {
        return null;
    }

    const lang = languageCode ? languageCode.slice(0, 2) : "he";
    const overlayTitle = getTranslation(lang, "News Flash");
    const overlayItems: SiteItemData[] = mapArticleExtraDataToOverlayItems(articles, verticalBackgroundImage).map(item => {
        const minutesSincePublished = Math.floor((new Date().getTime() - new Date(item.dateUpdatedOnSite).getTime()) / (1000 * 60));
        const isRecentItem = minutesSincePublished < (highlightingDuration || 1);
        const date = isRecentItem ? getTranslation(lang, "Now") : <DateDisplay date={item.dateUpdatedOnSite} isMinutesAgo={true} isArticle={true} />;
        return { ...item, date };
    });

    const sheetStyle = !isClosing && dragOffsetY > 0 ? { transform: `translateY(${dragOffsetY}px)` } : undefined;

    return (
        <div className={`ongoing-roof-event-popup-overlay ${isClosing ? "is-closing" : ""}`} onClick={onBackdropClick}>
            <div className="ongoing-roof-event-popup-sheet" onClick={e => e.stopPropagation()} style={sheetStyle}>
                <div className="ongoing-popup-handle-area" onTouchStart={onHandleTouchStart} onTouchMove={onHandleTouchMove} onTouchEnd={onHandleTouchEnd}>
                    <div className="ongoing-popup-handle" />
                </div>

                <div className="ongoing-popup-sticky-header">
                    <div className="ongoing-live-updates-label non-clickable">
                        <span className="label-dot" aria-hidden="true" />
                        <span className="label-text">{LIVE_UPDATES_LABEL}</span>
                    </div>

                    <div className="ongoing-popup-roof-title">{roof}</div>
                </div>

                <div className="ongoing-popup-scroll">
                    <div className="ongoing-popup-list">
                        <div className="ongoing-popup-list-connector" aria-hidden="true" />

                        {articles.map((article, index) => renderItem(article, index, index === 0))}
                    </div>
                </div>
            </div>

            <VerticalVideoOverlayPlayer
                isLTR={isLTR}
                domain={domain}
                isMobile={true}
                category={category}
                index={selectedVideo}
                isTitleVisible={true}
                showPrerollAds={false}
                isSubTitleVisible={true}
                videoItems={overlayItems}
                overlayTitle={overlayTitle}
                languageCode={languageCode}
                onVideoOverlayClose={closeMedia}
                isOverlayOpen={isMediaOverlayOpen}
                selectedVideoIndex={selectedVideo}
                ItemComponent={(playerProps) => <FlashesMediaItem {...playerProps} />}
            />
        </div>
    );
}
